import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Database, Upload, Search, UserPlus } from "lucide-react";

export default function Consumers() {
  const { user } = useAuth();
  const isOwner = user.role === "owner";
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ total: 0, items: [] });
  const [stats, setStats] = useState({ active: 0, backup: 0 });
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ consumer_no: "", name: "", mobile: "", area: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/consumers?search=${encodeURIComponent(search)}&limit=50`).then((r) => setData(r.data)).finally(() => setLoading(false));
    api.get("/consumers/stats").then((r) => setStats(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]);

  const doPreview = async () => {
    if (!file) { toast.error("Choose a CSV file"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/consumers/upload-preview", fd);
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const doCommit = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/consumers/upload-commit", fd);
      toast.success(`Applied: ${r.data.new} new, ${r.data.updated} updated, ${r.data.deleted} removed (backed up)`);
      setUploadOpen(false); setPreview(null); setFile(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const addConsumer = async () => {
    if (!form.consumer_no || !form.name) { toast.error("Consumer number and name required"); return; }
    setBusy(true);
    try {
      await api.post("/consumers", form);
      toast.success("Consumer added");
      setAddOpen(false); setForm({ consumer_no: "", name: "", mobile: "", area: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold flex items-center gap-2"><Database className="h-6 w-6 text-primary" /> Consumers</h1>
          <p className="text-sm text-muted-foreground">{stats.active.toLocaleString("en-IN")} active · {stats.backup} in backup</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAddOpen(true)} data-testid="consumer-add-button"><UserPlus className="h-4 w-4 mr-2" />Add</Button>
          {isOwner && <Button onClick={() => setUploadOpen(true)} data-testid="consumer-upload-open-button"><Upload className="h-4 w-4 mr-2" />Upload HP file</Button>}
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search by number, name or mobile" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="consumer-search-input" />
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Consumer No</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Area</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Package</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {!loading && data.items.map((c) => (
                <TableRow key={c.consumer_no}>
                  <TableCell className="font-mono">{c.consumer_no}</TableCell>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-muted-foreground">{c.area}</TableCell>
                  <TableCell className="font-mono text-sm">{c.mobile}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{c.package_desc}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {!loading && <div className="p-3 text-xs text-muted-foreground border-t">Showing {data.items.length} of {data.total.toLocaleString("en-IN")} matching consumers</div>}
      </Card>

      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!o) { setPreview(null); setFile(null); } setUploadOpen(o); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload HP master consumer file</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input type="file" accept=".csv" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} data-testid="consumer-upload-input" />
            {!preview && <Button onClick={doPreview} disabled={busy || !file} className="w-full">{busy ? "Reading…" : "Preview changes"}</Button>}
            {preview && (
              <div className="space-y-3" data-testid="consumer-upload-preview-table">
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2"><div className="text-lg font-semibold text-emerald-800">{preview.new}</div><div className="text-xs text-emerald-700">New</div></div>
                  <div className="rounded-lg bg-sky-50 border border-sky-200 p-2"><div className="text-lg font-semibold text-sky-800">{preview.updated}</div><div className="text-xs text-sky-700">Updated</div></div>
                  <div className="rounded-lg bg-rose-50 border border-rose-200 p-2"><div className="text-lg font-semibold text-rose-800">{preview.missing}</div><div className="text-xs text-rose-700">Removed</div></div>
                  <div className="rounded-lg bg-secondary p-2"><div className="text-lg font-semibold">{preview.total_in_file}</div><div className="text-xs text-muted-foreground">In file</div></div>
                </div>
                {preview.missing > 0 && <p className="text-xs text-rose-700">{preview.missing} consumers missing from this file will be removed from the active list (kept in a hidden backup).</p>}
                <Button onClick={doCommit} disabled={busy} className="w-full" data-testid="consumer-upload-import-button">{busy ? "Importing…" : "Confirm & import"}</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add consumer manually</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Consumer number</Label><Input value={form.consumer_no} onChange={(e) => setForm({ ...form, consumer_no: e.target.value })} className="font-mono" data-testid="add-consumer-no-input" /></div>
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-consumer-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
              <div><Label>Area</Label><Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={addConsumer} disabled={busy} data-testid="add-consumer-save-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
